import moment from "moment";
import { Box, Button, MenuItem, Paper, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { FC, useEffect, useState } from "react";
import {
  Contact,
  EventEntryInput,
  EventStatus,
  EventType,
  Location,
  MajorEventTimeToLiveInDays,
  Region,
  RoadwayDirection,
  TrafficControlType,
  TrafficImpact,
  TrafficImpactType,
} from "../../../reducers/states/event-management";
import ViewEditDate from "../../common/view-edit-date";
import ViewEditDatetime from "../../common/view-edit-datetime";
import ViewEditSelect from "../../common/view-edit-select";
import ViewEditTime from "../../common/view-edit-time";
import ViewEditTextField from "../../common/view-edit-text-field";
import { useAuthContext } from "../../../core";
import { createMockEvent } from "../../../__mocks__/event-management";
import MapLocationPoint from "./map-location-point";
import { IsMockEnabled } from "../../../utils/user-info-utils";
import { useSearchParams } from "react-router-dom";

export enum EventEntryFormMode {
  CREATE = "CREATE",
  EDIT = "EDIT",
  READ_ONLY = "READ_ONLY", // View mode and cannot switch to Edit mode
  VIEW = "VIEW", // View mode and can switch to Edit mode
}

interface EventEntryFormProps {
  props?: {
    mode: EventEntryFormMode;
    event?: EventEntryInput;
    onEventInputChanged?: (eventInput: EventEntryInput) => void;
  };
}

const EventEntryForm: FC<EventEntryFormProps> = ({ props }) => {
  const { authState } = useAuthContext();
  const [searchParams] = useSearchParams();
  const [isEdit, setIsEdit] = useState(
    !props || props.mode === EventEntryFormMode.CREATE || props.mode === EventEntryFormMode.EDIT,
  );
  const [eventInput, setEventInput] = useState(
    props?.event
      ? props.event
      : ({
          contacts: [{} as Contact],
          locations: [{} as Location],
          trafficImpacts: [{} as TrafficImpact],
        } as EventEntryInput),
  );

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const mode = props?.mode;
  useEffect(() => {
    setIsEdit(!mode || mode === EventEntryFormMode.CREATE || mode === EventEntryFormMode.EDIT);
  }, [mode]);

  const event = props?.event;
  useEffect(() => {
    event && setEventInput(event);
  }, [event]);

  const onEventInputChanged = props?.onEventInputChanged;
  useEffect(() => {
    onEventInputChanged && onEventInputChanged(eventInput);
  }, [eventInput, onEventInputChanged]);

  const getTimeToLive = (endDate: string): Number | undefined => {
    if(!endDate) return undefined;
    
    try {
      let date = new Date(endDate);
      date.setDate(date.getDate() + MajorEventTimeToLiveInDays);
      let ttlInSeconds = Math.floor(date.getTime() / 1000);
      return ttlInSeconds;
    } catch {
      return undefined;
    }
  }

  const handleInputChange = (e: any, field: string) => {
    let value: string = e as string;
    if (field.startsWith("locations.")) {
      const parts = field.split(".");
      const index = Number.parseInt(parts[1]);
      field = parts[2];

      setEventInput((current) => {
        let event = { ...current };
        let location = event?.locations?.[index] ?? ({} as Location);
        (location as any)[field] = value;

        return event;
      });
    } else if (field.startsWith("contact.")) {
      const parts = field.split(".");
      const index = Number.parseInt(parts[1]);
      field = parts[2];

      setEventInput((current) => {
        let event = { ...current };
        let contact = event?.contacts?.[index] ?? ({} as Contact);
        (contact as any)[field] = value;

        return event;
      });
    } else if (field.startsWith("impact.")) {
      const parts = field.split(".");
      const index = Number.parseInt(parts[1]);
      field = parts[2];

      setEventInput((current) => {
        let event = { ...current };
        let impact = event?.trafficImpacts?.[index] ?? ({} as TrafficImpact);

        if (field === "isContinuous") {
          (impact as any)[field] = value === "true"; // set boolean value
          if (value === "true") {
            impact.workDays = []; // Clear work days when isContinuous is set to true
          }
        } else {
          (impact as any)[field] = value; // set string value
        }

        return event;
      });
    } else if (field === "isMajorEvent") {
      setEventInput((current) => ({ ...current, [field]: value === "true" })); // set boolean value
    } else if (field === "endDate") {
      setEventInput((current) => ({
         ...current,
         [field]: value,
         TimeToLive: getTimeToLive(value),
        }));
    } else {
      setEventInput((current) => ({ ...current, [field]: value }));
    }
  };

  // todo: delete this testing functionality in the future.
  const MockInputButton = () => {
    return IsMockEnabled(authState.email, searchParams) && isEdit ? (
      <Button
        onClick={() => {
          setEventInput(
            createMockEvent({
              type: EventType.Contract,
              direction: RoadwayDirection.Both_Directions,
              commonName: "Hwy 2",
              status: EventStatus.Pending,
              startMilepost: 10,
              endMilepost: 25,
              contractNumber: "1",
            }),
          );
        }}
        color="inherit"
        variant="contained"
      >
        Fill w/Mock Input
      </Button>
    ) : null;
  };

  const Location = (location: Location, index: number) => {
    return (
      <Paper variant="outlined">
        <Grid
          container
          columns={{ xs: 1, sm: 2, md: isEdit ? 6 : 5 }}
          direction="row"
          justifyContent="flex-start"
          wrap="wrap"
          spacing={0}
          style={{ height: "100%", overflow: "auto" }}
        >
          <Grid xs={1}>
            <ViewEditTextField
              props={{
                id: `locations.${index}.stateRoute`,
                label: "SR",
                initialValue: location.stateRoute ?? "",
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>

          <Grid xs={1}>
            <ViewEditSelect
              id={`locations.${index}.direction`}
              label="Direction"
              value={location.direction ?? ""}
              isEdit
              handleInputChange={handleInputChange}
            >
              {Object.values(RoadwayDirection).map((direction) => {
                return <MenuItem value={direction}>{direction}</MenuItem>;
              })}
            </ViewEditSelect>
          </Grid>
          <Grid xs={1}>
            <ViewEditTextField
              props={{
                id: `locations.${index}.startMilepost`,
                label: "Start Milepost",
                initialValue: location.startMilepost?.toString() || "",
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>
          <Grid xs={1}>
            <ViewEditTextField
              props={{
                id: `locations.${index}.endMilepost`,
                label: "End Milepost",
                initialValue: location.endMilepost?.toString() ?? "",
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>
          <Grid xs={1}>
            <ViewEditTextField
              props={{
                id: `locations.${index}.commonName`,
                label: "Common Name",
                initialValue: location.commonName,
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>
          {isEdit && (
            <Grid xs={1} style={{ margin: "auto" }}>
              <Button
                color="inherit"
                variant="contained"
                onClick={() => {
                  deleteLocation(index);
                }}
              >
                Delete location
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  const addLocation = () => {
    setEventInput((current) => ({
      ...current,
      locations: current.locations.concat([{} as Location]),
    }));
  };

  const deleteLocation = (index: number) => {
    setEventInput((current) => ({
      ...current,
      locations: current.locations.filter((_, arrIndex) => {
        return index !== arrIndex;
      }),
    }));
  };

  const Contact = (contact: Contact, index: number) => {
    return (
      <Paper variant="outlined">
        <Grid
          container
          columns={{ xs: 1, sm: 2, md: isEdit ? 6 : 5 }}
          direction="row"
          justifyContent="flex-start"
          wrap="wrap"
          spacing={0}
          style={{ height: "100%", overflow: "auto" }}
        >
          <Grid xs={1}>
            <ViewEditTextField
              props={{
                id: `contact.${index}.name`,
                label: "Name",
                initialValue: contact.name ?? "",
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>
          <Grid xs={1}>
            <ViewEditTextField
              props={{
                id: `contact.${index}.title`,
                label: "Title",
                initialValue: contact.title ?? "",
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>
          <Grid xs={1}>
            <ViewEditTextField
              props={{
                id: `contact.${index}.priPhone`,
                label: "Primary Phone",
                initialValue: contact.priPhone ?? "",
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>
          <Grid xs={1}>
            <ViewEditTextField
              props={{
                id: `contact.${index}.altPhone`,
                label: "Alt. Phone",
                initialValue: contact.altPhone ?? "",
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>
          <Grid xs={1}>
            <ViewEditTextField
              props={{
                id: `contact.${index}.email`,
                label: "Email",
                initialValue: contact.email ?? "",
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>
          {isEdit && (
            <Grid xs={1} style={{ margin: "auto" }}>
              <Button
                color="inherit"
                variant="contained"
                onClick={() => {
                  deleteContact(index);
                }}
              >
                Delete contact
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  const addContact = () => {
    setEventInput((current) => ({
      ...current,
      contacts: current.contacts.concat([{} as Contact]),
    }));
  };

  const deleteContact = (index: number) => {
    setEventInput((current) => ({
      ...current,
      contacts: eventInput.contacts.filter((_, arrIndex) => {
        return index !== arrIndex;
      }),
    }));
  };

  const TrafficImpact = (impact: TrafficImpact, index: number) => {
    return (
      <>
        <Typography variant="h6" component="h5">
          Impact {impact.startDate && " starting " + moment(impact.startDate).format("M/D/YY")}
        </Typography>
        <Paper variant="outlined">
          <Typography variant="h6">Location</Typography>
          <MapLocationPoint
            props={{
              id: `impact.${index}.geometry`,
              isEdit: isEdit,
              isReadOnly: mode === EventEntryFormMode.READ_ONLY,
              geometry: impact.geometry,
              handleInputChange: handleInputChange,
            }}
          />
          <Grid
            container
            columns={{ xs: 1, sm: 2, md: 5 }}
            direction="row"
            justifyContent="flex-start"
            wrap="wrap"
            spacing={0}
            style={{ height: "100%", overflow: "auto" }}
          >
            <Grid xs={1}>
              <ViewEditTextField
                props={{
                  id: `impact.${index}.stateRoute`,
                  label: "SR",
                  initialValue: impact.stateRoute ?? "",
                  isEdit: isEdit,
                  characterLimit: 100,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
            <Grid xs={1}>
              <ViewEditSelect
                id={`impact.${index}.direction`}
                label={"Direction"}
                value={impact.direction ?? ""}
                isEdit
                handleInputChange={handleInputChange}
              >
                {Object.values(RoadwayDirection).map((direction) => {
                  return <MenuItem value={direction}>{direction}</MenuItem>;
                })}
              </ViewEditSelect>
            </Grid>
            <Grid xs={1}>
              <ViewEditTextField
                props={{
                  id: `impact.${index}.startMilepost`,
                  label: "Start Milepost",
                  initialValue: impact.startMilepost?.toString() ?? "",
                  isEdit: isEdit,
                  characterLimit: 100,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
            <Grid xs={1}>
              <ViewEditTextField
                props={{
                  id: `impact.${index}.endMilepost`,
                  label: "End Milepost",
                  initialValue: impact.endMilepost?.toString() ?? "",
                  isEdit: isEdit,
                  characterLimit: 100,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
            <Grid xs={1}>
              <ViewEditTextField
                props={{
                  id: `impact.${index}.commonName`,
                  label: "Common Name",
                  initialValue: impact.commonName ?? "",
                  isEdit: isEdit,
                  characterLimit: 100,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
            {/* End of Location part of impact */}

            <Grid xs={1}>
              <ViewEditSelect
                id={`impact.${index}.type`}
                label="Type"
                value={impact.type ?? ""}
                isEdit
                handleInputChange={handleInputChange}
              >
                {Object.values(TrafficImpactType).map((type) => {
                  return <MenuItem value={type}>{type}</MenuItem>;
                })}
              </ViewEditSelect>
            </Grid>
            <Grid xs={1}>
              <ViewEditTextField
                props={{
                  id: `impact.${index}.priority`,
                  label: "Priority",
                  initialValue: impact.priority ?? "",
                  isEdit: isEdit,
                  characterLimit: 100,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
            <Grid xs={1}>
              {/* todo: Weather Permitting Yes/No checkbox/boolean */}
              <ViewEditSelect
                id={`impact.${index}.status`}
                label="Status"
                value={impact.status ?? EventStatus.Pending}
                isEdit
                handleInputChange={handleInputChange}
              >
                <MenuItem value={EventStatus.Active}>{EventStatus.Active}</MenuItem>
                <MenuItem value={EventStatus.Pending}>{EventStatus.Pending}</MenuItem>
              </ViewEditSelect>
            </Grid>
          </Grid>
          <Typography variant="h6">Schedule</Typography>
          <Grid
            container
            columns={{ xs: 1, sm: 2, md: 4, lg: 6 }}
            direction="row"
            justifyContent="flex-start"
            wrap="wrap"
            spacing={0}
            style={{ height: "100%", overflow: "auto" }}
          >
            <Grid xs={1}>
              <ViewEditDate
                props={{
                  id: `impact.${index}.startDate`,
                  label: "Start Date",
                  value: impact.startDate ?? "",
                  isEdit: isEdit,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
            <Grid xs={1}>
              <ViewEditDate
                props={{
                  id: `impact.${index}.endDate`,
                  label: "End Date",
                  value: impact.endDate ?? "",
                  isEdit: isEdit,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
            <Grid xs={1}>
              <ViewEditTime
                props={{
                  id: `impact.${index}.startTime`,
                  label: "Start Time",
                  value: impact.startTime,
                  isEdit: isEdit,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
            <Grid xs={1}>
              <ViewEditTime
                props={{
                  id: `impact.${index}.endTime`,
                  label: "End Time",
                  value: impact.endTime,
                  isEdit: isEdit,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
            <Grid xs={1} md={0.5}>
              <ViewEditSelect
                id={`impact.${index}.isContinuous`}
                label={"Continuous"}
                value={impact.isContinuous?.toString() ?? ""}
                isEdit
                handleInputChange={handleInputChange}
              >
                <MenuItem key="Yes" value={true.toString()}>
                  Yes
                </MenuItem>
                <MenuItem key="No" value={false.toString()}>
                  No
                </MenuItem>
              </ViewEditSelect>
            </Grid>
            <Grid xs={1} md={1.5}>
              <ViewEditSelect
                id={`impact.${index}.workDays`}
                label="Work Days"
                value={impact.workDays ?? []}
                isEdit={isEdit && !impact.isContinuous}
                multiple={true}
                handleInputChange={handleInputChange}
                tooltip={impact.isContinuous
                  ? "Not applicable when the impact is continuous."
                  : "The days per week the traffic impact will occur between start and end date."}
              >
                {days.map((day) => (
                  <MenuItem key={day} value={day}>
                    {day}
                  </MenuItem>
                ))}
              </ViewEditSelect>
            </Grid>
          </Grid>
          <Grid
            container
            columns={{ xs: 1, sm: 2, md: 4, lg: 6 }}
            direction="row"
            wrap="wrap"
            spacing={0}
            style={{ height: "100%", overflow: "auto" }}
          >
            <Grid xs={1}>
              <ViewEditDate
                props={{
                  id: `impact.${index}.contingencyStartDate`,
                  label: "Contingency Start Date",
                  value: impact.contingencyStartDate ?? "",
                  isEdit: isEdit,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
            <Grid xs={1}>
              <ViewEditDate
                props={{
                  id: `impact.${index}.contingencyEndDate`,
                  label: "Contingency End Date",
                  value: impact.contingencyEndDate ?? "",
                  isEdit: isEdit,
                  handleInputChange: handleInputChange,
                }}
              />
            </Grid>
          </Grid>
          <Typography variant="h6">Details</Typography>
          <ViewEditTextField
            props={{
              id: `impact.${index}.workDescription`,
              label: "Description of Work",
              initialValue: impact.workDescription ?? "",
              isEdit: isEdit,
              characterLimit: 100,
              handleInputChange: handleInputChange,
            }}
          />

          <ViewEditSelect
            id={`impact.${index}.trafficControlRequired`}
            label="Description of Traffic Control Required"
            value={impact.trafficControlRequired ?? ""}
            isEdit
            handleInputChange={handleInputChange}
          >
            <MenuItem value={TrafficControlType.ShortTermRampClosures}>
              {TrafficControlType.ShortTermRampClosures}
            </MenuItem>
          </ViewEditSelect>

          <ViewEditTextField
            props={{
              id: `impact.${index}.comments`,
              label: "Comments",
              initialValue: impact.comments ?? "",
              isEdit: isEdit,
              characterLimit: 100,
              handleInputChange: handleInputChange,
            }}
          />

          {isEdit && (
            <div className="delete-traffic-impact-container">
              <Button
                color="inherit"
                variant="contained"
                onClick={() => {
                  deleteTrafficImpact(index);
                }}
              >
                Delete traffic impact
              </Button>
            </div>
          )}
        </Paper>
      </>
    );
  };

  const addTrafficImpact = () => {
    setEventInput((current) => ({
      ...current,
      trafficImpacts: current.trafficImpacts.concat([{} as TrafficImpact]),
    }));
  };

  const deleteTrafficImpact = (index: number) => {
    setEventInput((current) => ({
      ...current,
      trafficImpacts: current.trafficImpacts.filter((_, arrIndex) => {
        return index !== arrIndex;
      }),
    }));
  };

  const MessageForNone = (nameOfThing: string) => {
    return (
      <Box>
        {`No ${nameOfThing}s.` +
          (mode === EventEntryFormMode.READ_ONLY
            ? ""
            : ` Click ${isEdit ? "" : '"Edit" then click'} "Add ${nameOfThing}" to add one.`)}
      </Box>
    );
  };

  return (
    <Box className="event-entry-form">
      <Typography variant="h5" component="h4">
        Event Information
      </Typography>

      <MockInputButton />

      <Paper variant="outlined">
        <Grid
          container
          columns={{ xs: 1, sm: 4, md: 6 }}
          direction="row"
          justifyContent="flex-start"
          wrap="wrap"
          spacing={0}
          style={{ height: "100%", overflow: "auto" }}
        >
          <Grid xs={1} sm={3} md={4}>
            <ViewEditTextField
              props={{
                id: "eventName",
                label: "Event Name",
                initialValue: eventInput?.eventName,
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>
          <Grid xs={1} sm={1} md={2}>
            <ViewEditSelect
              id="isMajorEvent"
              label="Major Event/Construction ?"
              value={eventInput?.isMajorEvent?.toString() ?? false.toString()}
              isEdit
              handleInputChange={handleInputChange}
            >
              <MenuItem key="Yes" value={true.toString()}>
                Yes
              </MenuItem>
              <MenuItem key="No" value={false.toString()}>
                No
              </MenuItem>
            </ViewEditSelect>
          </Grid>
        </Grid>

        <Grid
          container
          columns={{ xs: 1, sm: 2, md: 3 }}
          direction="row"
          justifyContent="flex-start"
          wrap="wrap"
          spacing={0}
          style={{ height: "100%", overflow: "auto" }}
        >
          <Grid xs={1}>
            <ViewEditSelect
              id="region"
              label="Region"
              value={eventInput.region ?? ""}
              isEdit
              handleInputChange={handleInputChange}
            >
              {Object.values(Region).map((region) => {
                return <MenuItem value={region}>{region}</MenuItem>;
              })}
            </ViewEditSelect>
          </Grid>

          {/* todo: Counties. Maybe a drop down list with check boxes? Or type-in/auto complete? */}

          <Grid xs={1}>
            <ViewEditSelect
              id="type"
              label="Event Type"
              value={eventInput.type ?? ""}
              isEdit
              handleInputChange={handleInputChange}
            >
              {Object.values(EventType).map((eventType) => {
                return <MenuItem value={eventType}>{eventType}</MenuItem>;
              })}
            </ViewEditSelect>
          </Grid>

          <Grid xs={1}>
            <ViewEditTextField
              props={{
                id: "contractNumber",
                label: "Contract Number",
                initialValue: eventInput.contractNumber,
                isEdit: isEdit,
                characterLimit: 100,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>

          <Grid xs={1}>
            <ViewEditSelect
              id="status"
              label="Status"
              value={eventInput.status ?? ""}
              isEdit
              handleInputChange={handleInputChange}
            >
              <MenuItem value={EventStatus.Active}>{EventStatus.Active}</MenuItem>
              <MenuItem value={EventStatus.Pending}>{EventStatus.Pending}</MenuItem>
            </ViewEditSelect>
          </Grid>

          {/* todo: low cost enhancement checkbox/boolean */}

          <Grid xs={1}>
            <ViewEditDatetime
              props={{
                id: "startDate",
                label: "Start Date",
                initialValue: eventInput.startDate ?? "",
                isEdit: isEdit,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>

          <Grid xs={1}>
            <ViewEditDatetime
              props={{
                id: "endDate",
                label: "End Date",
                initialValue: eventInput.endDate ?? "",
                isEdit: isEdit,
                handleInputChange: handleInputChange,
              }}
            />
          </Grid>
        </Grid>

        <ViewEditTextField
          props={{
            id: "projectNotes",
            label: "Project Notes",
            initialValue: eventInput.projectNotes,
            isEdit: isEdit,
            characterLimit: 100,
            handleInputChange: handleInputChange,
          }}
        />
      </Paper>

      <Typography variant="h5" component="h4">
        Location
      </Typography>

      <MapLocationPoint
        props={{
          id: "geometry",
          isEdit: isEdit,
          isReadOnly: mode === EventEntryFormMode.READ_ONLY,
          geometry: eventInput.geometry,
          handleInputChange: handleInputChange,
        }}
      />

      {eventInput.locations.length
        ? eventInput.locations.map((location: Location, index: number) => {
            return Location(location, index);
          })
        : MessageForNone("location")}

      {isEdit && (
        <Button onClick={addLocation} color="inherit" variant="contained">
          Add location
        </Button>
      )}

      <Typography variant="h5" component="h4">
        Contacts
      </Typography>

      {eventInput.contacts.length
        ? eventInput.contacts.map((contact: Contact, index: number) => {
            return Contact(contact, index);
          })
        : MessageForNone("contact")}

      {isEdit && (
        <Button onClick={addContact} color="inherit" variant="contained">
          Add contact
        </Button>
      )}

      <Typography variant="h5" component="h4">
        Traffic Impacts
      </Typography>

      {eventInput.trafficImpacts.length
        ? eventInput.trafficImpacts.map((impact: TrafficImpact, index: number) => {
            return TrafficImpact(impact, index);
          })
        : MessageForNone("traffic impact")}

      {isEdit && (
        <Button onClick={addTrafficImpact} color="inherit" variant="contained">
          Add Traffic Impact
        </Button>
      )}

      {/* <Typography variant="h5" component="h4">Commercial Vehicle Restrictions</Typography>

      <Paper variant="outlined">
        <div>todo: Display commercial vehicle restrictions</div>
      </Paper> */}

      {/* 
    Only needed for display, not creation.

    <Typography variant="h5">Update Log</Typography>

    <div>todo: Display any update logs</div>
    
    */}
    </Box>
  );
};

export default EventEntryForm;
